import { useQuery } from '@tanstack/react-query';

import {
  LATEST_PRODUCT_UPDATES_COUNT,
  LATEST_PRODUCT_UPDATES_STALE_TIME_MS,
  LATEST_UPDATES_API_URL,
} from './constants';
import type { LatestUpdatesResponse } from './types';

export const getLatestProductUpdatesQueryKey = (
  perType: number = LATEST_PRODUCT_UPDATES_COUNT,
) => ['latestProductUpdates', perType] as const;

const isLatestUpdatesResponse = (
  data: unknown,
): data is LatestUpdatesResponse => {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as LatestUpdatesResponse).product_updates)
  );
};

const fetchLatestProductUpdates = async (
  perType: number,
  signal?: AbortSignal,
): Promise<LatestUpdatesResponse> => {
  const url = new URL(LATEST_UPDATES_API_URL);
  url.searchParams.set('per_type', String(perType));

  const response = await fetch(url.toString(), { signal });
  const data: unknown = await response.json();

  // WordPress currently returns a 404 status with a valid JSON body for this
  // endpoint, so validate the payload shape instead of relying on response.ok.
  if (!isLatestUpdatesResponse(data)) {
    throw new Error(
      `Failed to fetch latest product updates: ${response.status}`,
    );
  }

  return data;
};

export const useLatestProductUpdates = (
  perType: number = LATEST_PRODUCT_UPDATES_COUNT,
) => {
  return useQuery({
    queryKey: getLatestProductUpdatesQueryKey(perType),
    queryFn: ({ signal }) => fetchLatestProductUpdates(perType, signal),
    staleTime: LATEST_PRODUCT_UPDATES_STALE_TIME_MS,
    select: (data) => data.product_updates.slice(0, perType),
  });
};
